import { Heading, Stack } from '@chakra-ui/react';
import { ConditionalField, getCommodityOptions } from '@pelicargo/utils';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';

import { API } from '../../utils/api-typings';
import { SimpleCheckbox } from '../SimpleCheckbox';
import { SimpleContactSelect } from '../SimpleContactSelect';
import { SimpleInput } from '../SimpleInput';
import { SimpleSelect } from '../SimpleSelect';

type Props = {
  quote: API.GetQuote;
  fields: ConditionalField[];
};

const requiredIndicatorStyles = { '& .chakra-form__required-indicator': { display: 'none' } };

export const ConditionalFields = ({ quote, fields }: Props) => {
  const [generalString, setGeneralString] = useState('');
  const cargoType = quote.request.cargo_type;
  const cargoTypeSub = quote.request.cargo_type_sub;
  // Specific Cargo asks for the real commodity, so generic ones (General Goods, Consolidation, …) are left out
  const commodityOptions = useMemo(
    () => getCommodityOptions({ cargoType, cargoTypeSub, excludeGeneric: true }),
    [cargoType, cargoTypeSub],
  );

  const generalHelper = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.value?.toLowerCase?.()?.startsWith?.('general')) {
        setGeneralString('Try to be more specific, if you can!');
      } else if (generalString) {
        setGeneralString('');
      }
    },
    [generalString],
  );

  const renderField = useCallback(
    (field: ConditionalField) => {
      switch (field) {
        case ConditionalField.HS_CODE:
          return <SimpleInput key={field} name="hs_code" isRequired label="HS Code" />;
        case ConditionalField.IS_CONSOLIDATED:
          return <SimpleCheckbox key={field} name="is_consolidated" isRequired label="HAWB Required" />;
        case ConditionalField.COMMODITY:
          return (
            <SimpleSelect
              key={field}
              name="commodity"
              label="Specific Cargo"
              sx={requiredIndicatorStyles}
              placeholder="Commodity"
              isRequired
              options={commodityOptions}
              inputProps={{ onChange: generalHelper }}
              helper={generalString}
            />
          );
        case ConditionalField.EMERGENCY_CONTACT:
          return <SimpleContactSelect key={field} isRequired label="Emergency Contact" name="emergency_contact" />;
        case ConditionalField.IS_MILITARY_RELATED:
          return <SimpleCheckbox key={field} isRequired name="is_military_related" label="Is military related?" />;
        default: {
          const check: never = field;
          console.log('Switch not exhaustive!', check);
        }
      }
    },
    [commodityOptions, generalHelper, generalString],
  );

  if (fields?.length) {
    return (
      <Stack>
        <Heading variant="h5">{quote?.provider?.name || quote?.provider_id} Requested Info</Heading>
        {fields.map(renderField)}
      </Stack>
    );
  }
  return <></>;
};
